import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getVendorSessions,
  getActivities,
  deleteSession as deleteSessionApi,
} from '../../util/api';

// ─── Async Thunks ───

const fetchSessionsThunk = createAsyncThunk(
  'VendorSessionsPage/fetchSessions',
  async (_, { rejectWithValue }) => {
    try {
      const r = await getVendorSessions();
      return r.data || [];
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load sessions');
    }
  }
);

const fetchActivitiesThunk = createAsyncThunk(
  'VendorSessionsPage/fetchActivities',
  async (_, { rejectWithValue }) => {
    try {
      const r = await getActivities();
      return r.data || [];
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load activities');
    }
  }
);

const deleteSessionThunk = createAsyncThunk(
  'VendorSessionsPage/deleteSession',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await deleteSessionApi({ id });
      dispatch(fetchSessionsThunk());
      return id;
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to delete session');
    }
  }
);

// ─── Slice ───

const slice = createSlice({
  name: 'VendorSessionsPage',
  initialState: {
    sessions: [] as any[],
    sessionsLoading: false,
    sessionsError: null as string | null,
    activities: [] as any[],
    activitiesLoading: false,
    deleting: false,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSessionsThunk.pending, s => {
        s.sessionsLoading = true;
        s.sessionsError = null;
      })
      .addCase(fetchSessionsThunk.fulfilled, (s, a) => {
        s.sessionsLoading = false;
        s.sessions = a.payload;
      })
      .addCase(fetchSessionsThunk.rejected, (s, a) => {
        s.sessionsLoading = false;
        s.sessionsError = a.payload as string;
      })
      .addCase(fetchActivitiesThunk.pending, s => {
        s.activitiesLoading = true;
      })
      .addCase(fetchActivitiesThunk.fulfilled, (s, a) => {
        s.activitiesLoading = false;
        s.activities = a.payload;
      })
      .addCase(fetchActivitiesThunk.rejected, s => {
        s.activitiesLoading = false;
      })
      .addCase(deleteSessionThunk.pending, s => {
        s.deleting = true;
      })
      .addCase(deleteSessionThunk.fulfilled, s => {
        s.deleting = false;
      })
      .addCase(deleteSessionThunk.rejected, s => {
        s.deleting = false;
      });
  },
});

export default slice.reducer;

// ─── Exported Thunks ───

export const fetchVendorSessions = () => (dispatch: any) =>
  dispatch(fetchSessionsThunk());

export const fetchActivitiesForFilter = () => (dispatch: any) =>
  dispatch(fetchActivitiesThunk());

export const deleteExistingSession = (id: string) => (dispatch: any) =>
  dispatch(deleteSessionThunk(id));

// ─── loadData ───

export const loadData = () => (dispatch: any) =>
  Promise.all([
    dispatch(fetchSessionsThunk()),
    dispatch(fetchActivitiesThunk()),
  ]);

// ─── Selectors ───

export const selectSessions = (s: any) =>
  s.VendorSessionsPage?.sessions || [];
export const selectSessionsLoading = (s: any) =>
  s.VendorSessionsPage?.sessionsLoading || false;
export const selectActivities = (s: any) =>
  s.VendorSessionsPage?.activities || [];
export const selectDeleting = (s: any) =>
  s.VendorSessionsPage?.deleting || false;
